import React, { useEffect, useState, useCallback } from 'react';
import MemberVolunteerPointsTable from './MemberVolunteerPointsTable';
import PropTypes from 'prop-types';
import LoadingContent from 'components/NewDesign/LoadingContent';
import { toast } from 'react-toastify';
import { getVolunteerPoints } from 'api/reportsApi';

const MemberVolunteerPoints = ({ clubID, seasons, onDataChange }) => {
  const [selectedSeasonID, setSelectedSeasonID] = useState(null);
  const [volunteerPoints, setVolunteerPoints] = useState([]);
  const [filteredPoints, setFilteredPoints] = useState([]);

  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(true);

  // Filter and sort seasons - same logic as AthleteSeasonPoints
  const getFilteredAndSortedSeasons = useCallback(() => {
    // Filter out Draft seasons
    const filteredSeasons = seasons.filter(
      (season) => season.status !== 'Draft'
    );

    // Sort by end date (latest first)
    const sortedSeasons = filteredSeasons.sort((a, b) => {
      const endDateA = new Date(a.endDate);
      const endDateB = new Date(b.endDate);
      return endDateB - endDateA;
    });

    return sortedSeasons;
  }, [seasons]);

  useEffect(() => {
    const filteredAndSortedSeasons = getFilteredAndSortedSeasons();

    if (filteredAndSortedSeasons.length > 0) {
      // Find the Active season first
      const activeSeason = filteredAndSortedSeasons.find(
        (season) => season.status === 'Active'
      );

      if (activeSeason) {
        setSelectedSeasonID(activeSeason.seasonID);
      } else {
        // If no Active season, select the first one (latest end date)
        setSelectedSeasonID(filteredAndSortedSeasons[0].seasonID);
      }
    }
  }, [getFilteredAndSortedSeasons]);

  const fetchVolunteerPoints = useCallback(async () => {
    try {
      if (clubID && selectedSeasonID) {
        const response = await getVolunteerPoints(
          clubID || '',
          selectedSeasonID
        );
        const pointsData = response.data || [];
        setVolunteerPoints(pointsData?.volunteers);
      }
      setLoading(false);
    } catch (err) {
      console.error('[Season] Fetch seasons error: ', err);
      toast.error('Failed to load seasons');
      setLoading(false);
    }
  }, [clubID, selectedSeasonID]);

  useEffect(() => {
    if (clubID && selectedSeasonID) {
      fetchVolunteerPoints();
    }
  }, [fetchVolunteerPoints, clubID, selectedSeasonID]);

  useEffect(() => {
    if (volunteerPoints?.length) {
      if (searchText) {
        const results = volunteerPoints.filter((p) =>
          p.volunteerName.toLowerCase().includes(searchText.toLowerCase())
        );
        setFilteredPoints(results);
      } else {
        setFilteredPoints(volunteerPoints);
      }
    } else {
      setFilteredPoints([]);
    }
  }, [searchText, volunteerPoints]);

  // Notify parent component of data changes (for export button)
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        hasData: filteredPoints.length > 0,
        selectedSeasonID,
      });
    }
  }, [filteredPoints, selectedSeasonID, onDataChange]);

  return (
    <>
      <LoadingContent loading={loading}>
        <MemberVolunteerPointsTable
          volunteerPoints={filteredPoints}
          setSearchText={setSearchText}
        />
      </LoadingContent>
    </>
  );
};

MemberVolunteerPoints.propTypes = {
  clubID: PropTypes.string,
  seasons: PropTypes.array,
  onDataChange: PropTypes.func,
};

export default MemberVolunteerPoints;